import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MaterialImportDeleteForm from "./MaterialImportDeleteForm";
import MaterialImportUpdateForm from "./MaterialImportUpdateForm";
import { getMaterialImports } from "../../actions/materialImports";
import MaterialImportCreateForm from "./MaterialImportCreateForm";
import MaterialImportItem from "./MaterialImportItem";
import { Plus, Filter } from "react-feather";
import TableSpinner from "../layout/TableSpinner";
import CustomPaginationWithManyPages from "../layout/CustomPaginationWithManyPages";
import Select from 'react-select';
import { usePermission } from '../../hooks/usePermission';
import { getMaterialOptions } from '../../actions/material';
import { getSupplierOptions } from '../../actions/supplier';
import { getFirmOptionsForManagers } from '../../actions/user';
import FilterByDateRange from "../layout/FilterByDateRange";
import FilterWithTrashed from "../filters/FilterWithTrashed";
import FilterOnlyTrashed from "../filters/FilterOnlyTrashed";
import { Button, Modal } from 'react-bootstrap';
import SearchForm from "../layout/SearchForm";
import '../../styles/materialImports.css';

// Create a DeletedMaterialImportsFilterModal component similar to DeletedSalesFilterModal
const DeletedMaterialImportsFilterModal = ({ show, handleClose, getPageItems }) => {
    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>O'chirilgan material importlar filtrlari</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <h6 className="mb-3">O'chirilgan material importlar bilan ishlash:</h6>
                    <div className="mb-3">
                        <FilterWithTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Barcha importlarni o'chirilganlar bilan birga ko'rsatish
                        </small>
                    </div>
                    
                    <div className="mb-3">
                        <FilterOnlyTrashed getPageItems={getPageItems} />
                        <small className="text-muted d-block mt-1">
                            Faqat o'chirilgan importlarni ko'rsatish
                        </small>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <small>
                        <strong>Eslatma:</strong> Bu filtrlar bir-birini bekor qiladi. Agar ikkala filtr ham yoqilgan bo'lsa, oxirgi tanlangan filtr ishlatiladi.
                    </small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Yopish
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const MaterialImports = () => {
    const dispatch = useDispatch();
    const { userCan } = usePermission();
    const { materialImport } = useSelector(state => state);
    const { supplier } = useSelector(state => state);
    const { material } = useSelector(state => state);
    const { user } = useSelector(state => state);
    const auth = useSelector(state => state.auth);
    const { 
        materialImports, 
        loading, 
        numberOfPages, 
        currentPage,
        total_material_imports
    } = materialImport;
    const { suppliers_DATA } = supplier;
    const { material_options } = material;
    const { firm_options } = user;

    const [showCreate, setShowCreate] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);
    const [isLoading, setIsLoading] = useState(loading ?? false);

    // Get URL parameters to check for trashed filters
    const params = new URLSearchParams(window.location.search);
    const withTrashed = params.get('with_trashed') === 'true';
    const onlyTrashed = params.get('only_trashed') === 'true';
    const hasDeletedFilters = withTrashed || onlyTrashed;

    const handleCreateClose = () => setShowCreate(false);
    const handleCreateShow = () => setShowCreate(true);
    
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);
    
    const showLoader = () => setIsLoading(true);
    const hideLoader = () => setIsLoading(false);

    // Helper function to get filter parameters
    const getFilterParams = () => {
        const params = new URLSearchParams(window.location.search);
        const filters = {};
        
        for (const [key, value] of params.entries()) {
            filters[key] = value;
        }
        
        if (selectedSupplier) filters.supplier_id = selectedSupplier.value;
        if (selectedFirm) filters.firm_id = selectedFirm.value;
        if (selectedMaterial) filters.material_id = selectedMaterial.value;
        
        return filters;
    };

    // Fetch initial data when component mounts
    useEffect(() => {
        const initialFilters = getFilterParams();
        // Add per_page parameter to ensure pagination works correctly
        initialFilters.per_page = 10;
        dispatch(getMaterialImports(1, initialFilters));
    }, [dispatch]);

    useEffect(() => {
        dispatch(getSupplierOptions());
        dispatch(getMaterialOptions());
        // Load firm options for managers when component mounts
        if (auth.user?.role_name === 'manager') {
            dispatch(getFirmOptionsForManagers());
        }
    }, [dispatch, auth.user?.role_name]);

    useEffect(() => {
        if (selectedFirm) {
            dispatch(getMaterialOptions({ firm_id: selectedFirm.value }));
        } else {
            dispatch(getMaterialOptions());
        }
    }, [selectedFirm, dispatch]);

    if (!userCan('view material imports')) {
        return <div>Siz material importlarni ko'rish uchun ruxsatga ega emassiz.</div>;
    }

    const supplierOptions = suppliers_DATA ? suppliers_DATA.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) : [];

    const firmOptions = firm_options ? firm_options.map(firm => ({
        value: firm.id,
        label: firm.name
    })) : [];

    const materialOptions = material_options ? material_options.map(material => ({
        value: material.id,
        label: material.name
    })) : [];

    const handleSupplierChange = (selected) => {
        setSelectedSupplier(selected);
        const filters = getFilterParams();
        if (selected) {
            filters.supplier_id = selected.value;
        } else {
            delete filters.supplier_id;
        }
        filters.per_page = 10;
        dispatch(getMaterialImports(1, filters));
    };

    const handleFirmChange = (selected) => {
        setSelectedFirm(selected);
        const filters = getFilterParams();
        if (selected) {
            filters.firm_id = selected.value;
        } else {
            delete filters.firm_id;
        }
        filters.per_page = 10;
        dispatch(getMaterialImports(1, filters));
    };

    const handleMaterialChange = (selected) => {
        setSelectedMaterial(selected);
        const filters = getFilterParams();
        if (selected) {
            filters.material_id = selected.value;
        } else {
            delete filters.material_id;
        }
        filters.per_page = 10;
        dispatch(getMaterialImports(1, filters));
    };

    // Check if we have pagination data and total items
    const hasPaginationData = numberOfPages !== undefined && currentPage !== undefined;
    const hasItems = materialImports && materialImports.length > 0;
    const shouldShowPagination = hasPaginationData && (numberOfPages > 1 || total_material_imports > 10);

    return (
        <Fragment>
            <div className="container-fluid p-0">
                <div className="row mb-2">
                    <div className="col-md-9">
                        <h1 className="h3 mb-3">
                            <strong>Material importlari</strong> {total_material_imports ? `(${total_material_imports})` : ''}
                        </h1>
                        <div className="alert alert-info mb-3">
                            <small>
                                <strong>Eslatma:</strong> Faqat so'nggi 2 kun ichida yaratilgan material importlarni tahrirlash va o'chirish mumkin.
                                Eski importlarni faqat direktor tahrirlashi mumkin.
                            </small>
                        </div>
                    </div>
                    <div className="col-md-3 justify-content-end">
                        <div className="crud_actions text-end d-flex justify-content-end gap-2">
                            <button 
                                className={`btn ${hasDeletedFilters ? 'btn-warning' : 'btn-outline-secondary'}`} 
                                onClick={handleDeletedFiltersShow}
                            >
                                <Filter size={16} className="me-1" />
                                {hasDeletedFilters ? 'O\'chirilgan filtr faol' : 'O\'chirilgan filtr'}
                            </button>
                            
                            {userCan('create material imports') && (
                                <button className="btn btn-primary" onClick={handleCreateShow}>
                                    Import qo'shish <Plus size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-3 mb-0">
                        <Select
                            value={selectedSupplier}
                            onChange={handleSupplierChange}
                            options={supplierOptions}
                            isClearable
                            placeholder="Ta'minotchini tanlang..."
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className="col-md-3">
                        <Select
                            value={selectedFirm}
                            onChange={handleFirmChange}
                            options={firmOptions}
                            isClearable
                            placeholder="Firmani tanlang..."
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className="col-md-3">
                        <SearchForm getPageItems={getMaterialImports} />
                    </div>
                    <div className="col-md-3">
                        <FilterByDateRange getPageItems={getMaterialImports} />
                    </div>
                    <div className="col-md-3 mt-0">
                        <Select
                            value={selectedMaterial}
                            onChange={handleMaterialChange}
                            options={materialOptions}
                            isClearable
                            placeholder="Materialni tanlang..."
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>

                <div className="row mt-4">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <div className="card w-100 table-responsive">
                            
                            {loading && <TableSpinner/>}
                            <table className="table custom-table table-hover">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Referens</th>
                                    <th scope="col">Ta'minotchi</th>
                                    <th scope="col">Ombor/Firma</th>
                                    <th scope="col">Materiallar</th>
                                    <th scope="col">Jami summa</th>
                                    <th scope="col">Sana</th>
                                    <th scope="col">Yaratilgan sana</th>
                                    <th scope="col">Yaratuvchi</th>
                                    <th scope="col">Maxsus sotuv uchunmi?</th>
                                    <th scope="col">Amallar</th>
                                </tr>
                                </thead>
                                <tbody>
                                {materialImports?.map((materialImport, index) => (
                                    <MaterialImportItem
                                        key={materialImport.id}
                                        materialImport={materialImport}
                                        number={index + 1}
                                    />
                                ))}
                                </tbody>
                            </table>
                            
                            {/* Show message when no items found */}
                            {!loading && (!hasItems) && (
                                <div className="text-center p-3">
                                    <p>Material importlar topilmadi</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-12 col-xxl-12 d-flex justify-content-center">
                        {shouldShowPagination && (
                            <CustomPaginationWithManyPages
                                numberOfPages={numberOfPages}
                                getPageItems={getMaterialImports}
                                current_page={currentPage}
                                custom_params={{ per_page: 10 }}
                            />
                        )}
                    </div>
                </div>

            </div>

            <MaterialImportCreateForm show={showCreate} handleClose={handleCreateClose} />
            <MaterialImportUpdateForm />
            <MaterialImportDeleteForm />
            <DeletedMaterialImportsFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getMaterialImports}
            />
        </Fragment>
    );
};

export default MaterialImports;
